/**
 * Smart Model Selection Service
 * Optimizes AI costs by selecting the most appropriate model for each task
 *
 * Cost Optimization Strategy:
 * - Simple tasks: gpt-3.5-turbo (cheapest, fastest)
 * - Quality summaries: gpt-4-turbo (better reasoning, worth the cost)
 * - Conversations: gpt-4-turbo (good reasoning and context)
 * - Complex reasoning: gpt-4-turbo or gpt-4-turbo based on requirements
 */

export type TaskType =
  | "grammar" // Fix spelling & grammar
  | "improve" // Improve writing style
  | "tone" // Change tone/mood
  | "summary" // Generate professional summaries
  | "conversation" // AI assistant conversations
  | "longDocument" // Large document processing
  | "complexReasoning" // Multi-step reasoning tasks
  | "general"; // Default fallback

export type ModelRecommendation = {
  model: string;
  reason: string;
  estimatedCost: number; // Cost per 1K tokens (rough estimate)
  maxTokens: number;
  temperature: number;
};

export class ModelSelector {
  // Current OpenAI pricing (per 1M tokens)
  private static readonly MODEL_COSTS = {
    "gpt-3.5-turbo": { input: 0.5, output: 1.5 },
    "gpt-4-turbo": { input: 10, output: 30 },
    "gpt-4": { input: 30, output: 60 }, // Legacy, avoid
  } as const;

  /**
   * Select the optimal model for a given task
   */
  static getOptimalModel(
    taskType: TaskType,
    inputLength = 100,
    priorityLevel: "cost" | "quality" | "balanced" = "balanced",
  ): ModelRecommendation {
    switch (taskType) {
      case "grammar": {
        return {
          model: "gpt-3.5-turbo",
          reason: "Simple grammar fixes don't need advanced reasoning",
          estimatedCost: 0.002, // Very low cost
          maxTokens: 500,
          temperature: 0.1, // Precise corrections
        };
      }

      case "improve": {
        return {
          model: "gpt-3.5-turbo",
          reason: "Style improvements work well with gpt-3.5-turbo",
          estimatedCost: 0.002,
          maxTokens: 800,
          temperature: 0.3, // Some creativity
        };
      }

      case "tone": {
        return {
          model: "gpt-3.5-turbo",
          reason: "Tone changes are straightforward transformations",
          estimatedCost: 0.002,
          maxTokens: 600,
          temperature: 0.5, // Moderate creativity
        };
      }

      case "summary": {
        if (priorityLevel === "cost") {
          return {
            model: "gpt-3.5-turbo",
            reason: "Cost-optimized summary generation",
            estimatedCost: 0.002,
            maxTokens: 1000,
            temperature: 0.7,
          };
        }
        return {
          model: "gpt-4-turbo",
          reason: "Professional summaries benefit from advanced reasoning",
          estimatedCost: 0.02, // Higher cost but better quality
          maxTokens: 1000,
          temperature: 0.7,
        };
      }

      case "conversation": {
        if (inputLength > 50_000) {
          return {
            model: "gpt-4-turbo",
            reason: "Large context needed, gpt-4-turbo has good context window",
            estimatedCost: 0.01,
            maxTokens: 2000,
            temperature: 0.7,
          };
        }
        return {
          model: "gpt-4-turbo",
          reason: "Better reasoning and good context window",
          estimatedCost: 0.01,
          maxTokens: 1500,
          temperature: 0.7,
        };
      }

      case "longDocument": {
        if (inputLength > 100_000) {
          return {
            model: "gpt-4-turbo",
            reason: "Good context window for long documents",
            estimatedCost: 0.01,
            maxTokens: 3000,
            temperature: 0.5,
          };
        }
        return {
          model: "gpt-4-turbo",
          reason: "Good balance for large documents under 100K tokens",
          estimatedCost: 0.02,
          maxTokens: 2000,
          temperature: 0.5,
        };
      }

      case "complexReasoning": {
        if (priorityLevel === "cost") {
          return {
            model: "gpt-3.5-turbo",
            reason: "Cost-optimized reasoning",
            estimatedCost: 0.002,
            maxTokens: 2000,
            temperature: 0.3,
          };
        }
        return {
          model: "gpt-4-turbo",
          reason: "Best reasoning capabilities for complex tasks",
          estimatedCost: 0.02,
          maxTokens: 2000,
          temperature: 0.3,
        };
      }

      case "general":
      default: {
        return {
          model: "gpt-3.5-turbo",
          reason: "Safe default for unknown tasks",
          estimatedCost: 0.002,
          maxTokens: 1000,
          temperature: 0.7,
        };
      }
    }
  }

  /**
   * Get model recommendation with cost analysis
   */
  static getModelWithCostAnalysis(
    taskType: TaskType,
    inputTokens: number,
    expectedOutputTokens = 200,
  ) {
    const recommendation = this.getOptimalModel(taskType, inputTokens);
    const costs = this.MODEL_COSTS[recommendation.model as keyof typeof this.MODEL_COSTS];

    if (!costs) {
      throw new Error(`Unknown model: ${recommendation.model}`);
    }

    const estimatedCost =
      (inputTokens / 1_000_000) * costs.input + (expectedOutputTokens / 1_000_000) * costs.output;

    return {
      ...recommendation,
      actualEstimatedCost: estimatedCost,
      costBreakdown: {
        input: (inputTokens / 1_000_000) * costs.input,
        output: (expectedOutputTokens / 1_000_000) * costs.output,
        total: estimatedCost,
      },
    };
  }

  /**
   * Compare costs between models for a specific task
   */
  static compareCosts(taskType: TaskType, inputTokens: number, outputTokens = 200) {
    const models = ["gpt-3.5-turbo", "gpt-4-turbo"] as const;

    return models
      .map((model) => {
        const costs = this.MODEL_COSTS[model];
        const totalCost =
          (inputTokens / 1_000_000) * costs.input + (outputTokens / 1_000_000) * costs.output;

        return {
          model,
          totalCost,
          inputCost: (inputTokens / 1_000_000) * costs.input,
          outputCost: (outputTokens / 1_000_000) * costs.output,
          recommended: this.getOptimalModel(taskType, inputTokens).model === model,
        };
      })
      .sort((a, b) => a.totalCost - b.totalCost);
  }

  /**
   * Feature flag support for gradual rollout
   */
  static getModelWithFeatureFlag(
    taskType: TaskType,
    inputLength = 100,
    useOptimizedModels = true,
  ): ModelRecommendation {
    if (!useOptimizedModels) {
      // Fallback to safe default
      return {
        model: "gpt-3.5-turbo",
        reason: "Feature flag disabled, using safe default",
        estimatedCost: 0.002,
        maxTokens: 1000,
        temperature: 0.7,
      };
    }

    return this.getOptimalModel(taskType, inputLength);
  }
}

// Export convenience functions
export const getOptimalModel = ModelSelector.getOptimalModel;
export const getModelWithCostAnalysis = ModelSelector.getModelWithCostAnalysis;
export const compareCosts = ModelSelector.compareCosts;
